import React, { useEffect, useState } from "react";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, CartesianGrid
} from "recharts";
import axios from "axios";
import { decryptfromHEX } from "../../routes/encodedecode";

function DemographicsSummary({ cohortId = null }) {
  // Decrypt orgId from URL
  const queryParams = new URLSearchParams(window.location.search);
  const encrypted = queryParams.get("p");
  let orgId = null;
  if (encrypted) {
    try {
      const decrypted = decryptfromHEX(encrypted);
      const parsed = JSON.parse(decrypted);
      orgId = parsed.orgID;
      console.log("Decrypted orgID:", orgId);
    } catch (err) {
      console.error("Failed to decrypt orgID from URL:", err);
    }
  }

  // State for fetched data
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState({
    age: [],
    gender: [],
    ethnicity: [],
    location: [],
    cohort_size: 0,
    total_posts: 0,
  });

  // Fallback static data (for error or initial render)
  const fallback = {
    age: [
      { age: 15, count: 45 }, { age: 16, count: 40 }, { age: 17, count: 32 },
      { age: 18, count: 43 }, { age: 19, count: 34 }, { age: 20, count: 39 },
      { age: 21, count: 30 }, { age: 22, count: 9 }, { age: 23, count: 7 },
      { age: 24, count: 6 }, { age: 25, count: 5 },
    ],
    gender: [
      { name: "Female", count: 60 },
      { name: "Male", count: 40 },
    ],
    ethnicity: [
      { name: "Caucasian or white", count: 50 },
      { name: "Asian", count: 20 },
      { name: "Black or African American", count: 10 },
      { name: "American Indian or Alaska Native", count: 5 },
      { name: "Hispanic or Latinx", count: 10 },
      { name: "Prefer not to say", count: 5 },
    ],
    location: [
      { state: "FL", count: 45 }, { state: "GA", count: 38 }, { state: "NY", count: 35 },
      { state: "KY", count: 32 }, { state: "AL", count: 30 }, { state: "CA", count: 28 },
      { state: "AK", count: 20 }, { state: "AZ", count: 8 }, { state: "CO", count: 5 },
      { state: "CT", count: 4 }, { state: "HI", count: 3 }, { state: "ID", count: 3 },
      { state: "IL", count: 3 }, { state: "NC", count: 3 },
    ],
    cohort_size: 100,
    total_posts: 2000,
  };

  // Chart colors
  const genderColors = ["#4F8DFD", "#CFEFFF"];
  const ethnicityColors = [
    "#5C76E0", // (Caucasian or white)
    "#FFD1DC", // (Asian)
    "#B2B0D5", // (Black or African American)
    "#E0C79A", // (American Indian or Alaska Native)
    "#FF4D4D", // (Hispanic or Latinx)
    "#CFEFFF", // (Prefer not to say)
  ];

  useEffect(() => {
    setLoading(true);
    setError(null);
    let url = "https://genz-staging.feeltiptop.com/api/social_glass/demographics-summary";
    const params = [];
    if (cohortId) params.push(`cohort_id=${cohortId}`);
    if (orgId) params.push(`p=${orgId}`);
    if (params.length) url += `?${params.join("&")}`;
    axios
      .get(url)
      .then((res) => {
        setData(res.data);
        setLoading(false);
      })
      .catch((err) => {
        setError("Failed to load demographics");
        setData(fallback);
        setLoading(false);
      });
  }, [cohortId, orgId]);

  // Custom legend renderers for compact, left-aligned legends
  const renderSmallLegend = (items) => (
    <ul className="flex flex-col gap-0 text-xs pl-0 ml-0">
      {items.map((entry, idx) => (
        <li key={`item-${idx}`} className="flex items-center gap-2 text-[8px]">
          <span
            style={{
              background: entry.color,
              width: 14,           
              height: 6,          
              borderRadius: 1,      
              display: 'inline-block'
            }}
          />
          <span className="truncate max-w-[80px]">{entry.value}</span>
        </li>
      ))}
    </ul>
  );

  if (loading) {
    return (
      <div className="w-full mb-8 flex items-center justify-center py-12 text-lg text-gray-500">Loading demographics...</div>
    );
  }

  if (error) {
    // Optionally show error message
  }

  return (
    <div className="w-full mb-2 p-6">
      {/* Header Row */}
      <div className="mb-6 flex justify-between items-start">
        <div className="flex flex-col items-start">
          <h1 className="text-2xl font-bold">Session Demographics</h1>
          <span className="mt-2 rounded-full bg-gray-100 px-6 py-2 text-lg italic font-medium shadow-sm">
            {data.cohort_size} Cohort Members
          </span>
        </div>

        <span className="rounded-full bg-red-100 px-6 py-2 text-md font-normal text-[#DB4225] shadow-sm whitespace-nowrap">
          {data.total_posts.toLocaleString()} Total Social Post Submissions
        </span>
      </div>
      {/* Charts Row */}
      <div className="grid grid-cols-4 gap-6">
        <div className="grid grid-cols-4 gap-6">
          {/* Age Chart */}
          <div className="bg-white rounded-xl shadow-[0_4px_18px_rgba(0,0,0,0.05),0_-4px_18px_rgba(0,0,0,0.05)] p-2 flex flex-col w-full h-[180px] overflow-hidden col-span-4">
            <div className="text-left text-[12px] font-normal text-black ml-2 mb-2">
              Age
            </div>
            <div className="w-full h-auto flex justify-start">
              <ResponsiveContainer width="100%" height={150}>
                <BarChart
                  data={data.age}
                  margin={{ top: 0, right: 0, left: -35, bottom: 0 }} 
                  barCategoryGap="10%"
                >
                  <CartesianGrid strokeDasharray="none" stroke="#e5e7eb" strokeWidth={1} />
                  <XAxis
                    dataKey="age"
                    tick={{ fontSize: 8, fill: '#000' }}
                    axisLine={{ stroke: '#e5e7eb', strokeWidth: 1 }}
                    tickLine={false}
                  />
                  <YAxis
                    tick={{ fontSize: 8, fill: '#000' }}
                    axisLine={{ stroke: '#e5e7eb', strokeWidth: 1 }}
                    tickLine={false}
                    domain={[0, 60]}
                    ticks={Array.from({ length: 6 }, (_, i) => i * 10)} 
                    allowDecimals={false} 
                  />
                  <Tooltip />
                  <Bar
                    dataKey="count"
                    fill="#DB4225"
                    radius={[2, 2, 0, 0]}
                    stroke="none"
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>

        {/* Gender Chart */}
        <div className="bg-white rounded-xl shadow-[0_4px_18px_rgba(0,0,0,0.05),0_-04px_18px_rgba(0,0,0,0.05)] px-2 py-1 flex flex-row items-center h-[180px]">
          <div className="items-start justify-between h-full mr-2">
            <div className="font-normal mt-1 mb-2 text-left text-[12px]">Gender</div>
            <div className="mb-2">
              {renderSmallLegend(data.gender.map((d, i) => ({ value: d.name, color: genderColors[i % genderColors.length] })))}
            </div>
          </div>
          <div className="flex-1 h-full flex items-center justify-center">
            <ResponsiveContainer width={"100%"} height={"100%"}>
              <PieChart>
                <Pie
                  paddingAngle={0}
                  data={data.gender}
                  dataKey="count"
                  nameKey="name"
                  cx="50%"
                  cy="50%"
                  innerRadius={35}
                  outerRadius={55}
                  fill="#8884d8"
                  label={({ cx, cy, midAngle, innerRadius, outerRadius, percent }) => {
                    const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
                    const x = cx + radius * Math.cos(-midAngle * Math.PI / 180);
                    const y = cy + radius * Math.sin(-midAngle * Math.PI / 180);

                    return (
                      <text
                        x={x}
                        y={y}
                        fill="#000"
                        textAnchor="middle"
                        dominantBaseline="central"
                        fontSize={8}
                      >
                        {`${(percent * 100).toFixed(0)}%`}
                      </text>
                    );
                  }}
                  labelLine={false}
                >
                  {data.gender.map((entry, idx) => (
                    <Cell
                      key={`cell-gender-${idx}`}
                      fill={genderColors[idx % genderColors.length]}
                      stroke={genderColors[idx % genderColors.length]}
                      strokeWidth={1}
                    />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Ethnicity Chart */}
        <div className="bg-white rounded-xl shadow-[0_4px_18px_rgba(0,0,0,0.05),0_-04px_18px_rgba(0,0,0,0.05)] px-2 py-1 flex flex-row items-center h-[180px]">
          <div className="items-start justify-between h-[180px] mr-2 min-w-[80px]">
            <div className="font-normal mt-1 mb-2 text-left text-[12px]">Ethnicity</div>
            <div className="mb-2">
              {renderSmallLegend(data.ethnicity.map((d, i) => ({ value: d.name, color: ethnicityColors[i % ethnicityColors.length] })))}
            </div>
          </div>
          <div className="flex-1 h-full flex items-center justify-center">
            <ResponsiveContainer width={"100%"} height={"100%"}>
              <PieChart>
                <Pie
                  paddingAngle={0}
                  data={data.ethnicity}
                  dataKey="count"
                  nameKey="name"
                  cx="50%"
                  cy="50%"
                  innerRadius={30} // adjusted inner radius
                  outerRadius={50} // adjusted outer radius
                  fill="#8884d8"
                  label={({ cx, cy, midAngle, innerRadius, outerRadius, percent }) => {
                    const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
                    const x = cx + radius * Math.cos(-midAngle * Math.PI / 180);
                    const y = cy + radius * Math.sin(-midAngle * Math.PI / 180);

                    return (
                      <text
                        x={x}
                        y={y}
                        fill="#000"
                        textAnchor="middle"
                        dominantBaseline="central"
                        fontSize={8}
                      >
                        {`${(percent * 100).toFixed(0)}%`}
                      </text>
                    );
                  }}
                  labelLine={false}
                >
                  {data.ethnicity.map((entry, idx) => (
                    <Cell
                      key={`cell-ethnicity-${idx}`}
                      fill={ethnicityColors[idx % ethnicityColors.length]}
                      stroke={ethnicityColors[idx % ethnicityColors.length]}
                      strokeWidth={1}
                    />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
        {/* Location Chart */}
         <div className="grid grid-cols-4 gap-6">
        <div className="bg-white rounded-xl shadow-[0_4px_18px_rgba(0,0,0,0.05),0_-4px_18px_rgba(0,0,0,0.05)] p-2 flex flex-col w-full h-[180px] overflow-hidden col-span-4">
          <div className="text-left text-[12px] font-normal text-black ml-2 mb-2">
            Location
          </div>
          <div className="w-full h-auto flex justify-start">
            <ResponsiveContainer width="100%" height={150}>
              <BarChart
                data={data.location}
                margin={{ top: 0, right: 0, left: -35, bottom: 0 }}
                barCategoryGap="10%"
              >
                <CartesianGrid strokeDasharray="none" stroke="#e5e7eb" strokeWidth={1} />
                <XAxis
                  dataKey="state"
                  tick={{ fontSize: 8, fill: '#000' }}
                  axisLine={{ stroke: '#e5e7eb', strokeWidth: 1 }}
                  tickLine={false}
                />
                <YAxis
                  tick={{ fontSize: 8, fill: '#000' }}
                  axisLine={{ stroke: '#e5e7eb', strokeWidth: 1 }}
                  tickLine={false}
                  domain={[0, 60]}
                  ticks={Array.from({ length: 6 }, (_, i) => i * 10)}
                  allowDecimals={false}
                />
                <Tooltip />
                <Bar
                  dataKey="count"
                  fill="#BEBEEC"
                  radius={[2, 2, 0, 0]}
                  stroke="none"
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
      </div>
    </div>
  );
}

export default DemographicsSummary; 